import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus, Trash01 } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CustomPagination from '../../components/forms/Pagination';
import {
  TaskTemplatesClient,
  TaskTemplateDto,
  TaskCategoriesClient,
  TaskCategoryDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
} from '../../app/services/api/tasksClient';

const templatesClient = TaskTemplatesClient;
const categoriesClient = TaskCategoriesClient;
const prioritiesClient = TaskPrioritiesClient;

const TaskTemplatesPage: React.FC = () => {
  const { t } = useTranslation('tasks');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['TaskTemplatesView']) : false;
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskTemplatesCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['TaskTemplatesEdit']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['TaskTemplatesDelete']) : false;

  const [templates, setTemplates] = useState<TaskTemplateDto[]>([]);
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);

  const [searchName, setSearchName] = useState('');
  const [searchDescription, setSearchDescription] = useState('');
  const [searchCategoryId, setSearchCategoryId] = useState<number | ''>('');
  const [searchPriorityId] = useState<number | ''>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplateDto | null>(null);

  useEffect(() => {
    if (!canView) return;
    // Always refresh data whenever this page is (re)entered
    templatesClient.getTemplates().then(setTemplates).catch(() => setTemplates([]));
    categoriesClient.getCategories().then(setCategories).catch(() => setCategories([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));
  }, [canView, location.pathname]);

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/task-templates/new');
  };

  const handleOpenEdit = (row: TaskTemplateDto) => {
    if (!canEdit) return;
    navigate(`/task-templates/${row.id}`);
  };

  const handleRequestDelete = (row: TaskTemplateDto) => {
    if (!canDelete) return;
    setTemplateToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    await templatesClient.deleteTemplate(templateToDelete.id);
    const updated = await templatesClient.getTemplates();
    setTemplates(updated);
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      if (searchName && !template.name.toLowerCase().includes(searchName.toLowerCase())) return false;
      if (searchDescription && !(template.description ?? '').toLowerCase().includes(searchDescription.toLowerCase())) return false;
      if (searchCategoryId && template.taskCategoryId !== Number(searchCategoryId)) return false;
      return true;
    });
  }, [templates, searchName, searchDescription, searchCategoryId, searchPriorityId]);

  const handleClearFilters = () => {
    setSearchDescription('');
    setSearchCategoryId('');
    setPage(0);
  };

  const pagedTemplates = useMemo(
    () => filteredTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredTemplates, page, rowsPerPage],
  );

  const maxPages = Math.ceil(filteredTemplates.length / rowsPerPage);

  const getCategoryName = (id?: number | null) =>
    categories.find(c => c.id === id)?.name ?? '';

  // const getPriorityName = (id: number) =>
  //   priorities.find(p => p.id === id)?.name ?? '';

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('templates.title', 'Task Templates')}</PageTitle>
        <Pill text={t('templates.noPermission', 'You do not have permission to view task templates.')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle>{t('templates.title', 'Task Templates')}</PageTitle>
        </Grid>

        {/* Quick filters + search row */}
        <Grid item
          xs={12}
          md={9}>
          <Grid container
            spacing={2}
            alignItems='center'>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                placeholder={t('templates.searchByNamePlaceholder', 'Search by name')}
                fullWidth
                size='small'
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end', alignItems: 'flex-start' }}>
          {canCreate && (
            <Button
              size='lg'
              variant='primary'
              startIcon={<Plus />}
              onClick={handleOpenCreate}
            >
              {t('templates.createButton', 'Create Template')}
            </Button>
          )}
        </Grid>

        {/* Advanced filters accordion */}
        <Grid item
          xs={12}>
          <Accordion
            variant='borderless'
            expanded={isAdvancedOpen}
            onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
          >
            <AccordionSummary>
              <Grid container
                alignItems='center'
                justifyContent='space-between'
                sx={{ width: '100%' }}>
                <Grid item>
                  <Grid container
                    spacing={1}
                    alignItems='center'>
                    <Grid item>
                      {t('templates.advancedFilters', 'Advanced filters')}
                    </Grid>
                    <Grid item
                      style={{ marginTop: '8px' }}>
                      {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    size='md'
                    variant='secondary'
                    onClick={handleClearFilters}
                  >
                    {t('templates.clearFilters', 'Clear filters')}
                  </Button>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container
                spacing={3}>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    label={t('templates.filterDescription', 'Description')}
                    fullWidth
                    size='small'
                    value={searchDescription}
                    onChange={e => setSearchDescription(e.target.value)}
                  />
                </Grid>
                <Grid item
                  lg={3}
                  sm={6}
                  xs={12}>
                  <TextField
                    select
                    label={t('templates.filterCategory', 'Category')}
                    fullWidth
                    size='small'
                    value={searchCategoryId}
                    onChange={e => setSearchCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    <MenuItem value=''>{t('templates.filterAllCategories', 'All')}</MenuItem>
                    {categories.map(c => (
                      <MenuItem key={c.id}
                        value={c.id}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {t('templates.tableTitle', 'Task Templates')}
              <Pill text={t('templates.itemCount', '{{count}} Templates', { count: filteredTemplates.length })} />
            </TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('templates.columnName', 'Name')}</TableCell>
                    <TableCell>{t('templates.columnDescription', 'Description')}</TableCell>
                    <TableCell>{t('templates.columnCategory', 'Category')}</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedTemplates.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{getCategoryName(row.taskCategoryId)}</TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        {canDelete && (
                          <Trash01
                            className='delete-icon'
                            onClick={() => handleRequestDelete(row)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}
                        style={{ textAlign: 'center' }}>{t('templates.noTemplatesFound', 'No templates found.')}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Section>
        </Grid>
      </Grid>
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>{t('templates.deleteDialogTitle', 'Delete task template?')}</DialogTitle>
        <DialogContent>
          {t('templates.deleteDialogBody', 'Are you sure you want to delete the template "{{name}}"? This action cannot be undone.', { name: templateToDelete?.name ?? '' })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>{t('templates.cancelButton', 'Cancel')}</Button>
          <Button
            variant='primary'
            onClick={handleConfirmDelete}
          >
            {t('templates.deleteButton', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TaskTemplatesPage;
